import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { $log } from '@/common/api/log.service';
import { carrierOf } from '@/common/medicationSchedule';
import { useAuthStore } from '@/stores/auth';
import { computed, ref } from 'vue';
import { useScriptSureApi } from './useScriptSureApi';

/**
 * Strip non-numeric characters from phone number and truncate to 10 digits
 * @param {string} phone - The phone number
 * @returns {string} Formatted phone number
 */
const formatPhoneForScriptSure = (phone) => {
    if (!phone) return '';
    return phone.replace(/\D/g, '').slice(0, 10);
};

/**
 * Map patient gender to ScriptSure single character format
 * M = Male, F = Female, O = Other, U = Unknown
 * @param {string} gender - The FHIR gender string
 * @returns {string} Single character gender code
 */
const mapGenderToScriptSure = (gender) => {
    const genderMap = {
        male: 'M',
        female: 'F',
        other: 'O',
        unknown: 'U',
    };
    return genderMap[gender?.toLowerCase()] || 'U';
};

/**
 * Remove empty string, null, and undefined values from an object
 * @param {Object} obj - The object to clean
 * @returns {Object} Object with empty values removed
 */
const removeEmptyValues = (obj) => {
    return Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== '' && value !== null && value !== undefined));
};

/**
 * Validate patient data for ScriptSure registration
 * @param {Object} patient - The Patient resource
 * @returns {string[]} Array of missing/invalid field names (empty if valid)
 */
const validatePatientForScriptSure = (patient) => {
    const errors = [];

    if (!patient.firstName?.trim()) {
        errors.push('First name');
    } else if (patient.firstName.length > 35) {
        errors.push('First name must be 35 characters or less');
    }

    if (!patient.lastName?.trim()) {
        errors.push('Last name');
    } else if (patient.lastName.length > 35) {
        errors.push('Last name must be 35 characters or less');
    }

    if (!patient.birthDate) {
        errors.push('Date of birth');
    }

    if (!patient.gender || patient.gender === 'unknown') {
        errors.push('Gender');
    }

    const defaultAddress = patient.defaultAddress;
    if (!defaultAddress) {
        errors.push('Address');
    } else {
        if (!defaultAddress.line1?.trim()) {
            errors.push('Address line 1');
        } else if (defaultAddress.line1.length > 35) {
            errors.push('Address line 1 must be 35 characters or less');
        }

        if (!defaultAddress.city?.trim()) {
            errors.push('City');
        } else if (defaultAddress.city.length > 35) {
            errors.push('City must be 35 characters or less');
        }

        if (!defaultAddress.state?.trim()) {
            errors.push('State');
        } else if (defaultAddress.state.length > 2) {
            errors.push('State must be 2 characters or less');
        }

        if (!defaultAddress.postalCode?.trim()) {
            errors.push('Zip code');
        } else if (defaultAddress.postalCode.replace(/\D/g, '').length > 9) {
            errors.push('Zip code must be 9 digits or less');
        }
    }

    const homePhone = formatPhoneForScriptSure(patient.homePhoneValue);
    const workPhone = formatPhoneForScriptSure(patient.workPhoneValue);
    const cellPhone = formatPhoneForScriptSure(patient.mobilePhoneValue);

    if (!homePhone && !workPhone && !cellPhone) {
        errors.push('Home, work, or cell phone number');
    }

    return errors;
};

/**
 * Build sign-in data object from a Patient resource and prescriber context
 * @param {Object} patient - The Patient resource
 * @param {Object} prescriberContext - { practiceId, prescriberId, isActingOnBehalf }
 * @returns {Object} The sign-in payload for the ScriptSure API
 */
const buildSignInDataForPatient = (patient, prescriberContext) => {
    const defaultAddress = patient.defaultAddress;

    const homePhone = formatPhoneForScriptSure(patient.homePhoneValue);
    const workPhone = formatPhoneForScriptSure(patient.workPhoneValue);
    const cellPhone = formatPhoneForScriptSure(patient.mobilePhoneValue);

    const officialName = patient.name?.find((n) => n.use === 'official');
    const suffix = officialName?.suffix?.[0] || '';

    const data = {
        // Prescriber context
        practiceId: prescriberContext.practiceId,
        prescriberId: prescriberContext.prescriberId,
        isActingOnBehalf: prescriberContext.isActingOnBehalf,

        // Required patient fields
        firstName: patient.firstName?.substring(0, 35),
        lastName: patient.lastName?.substring(0, 35),
        dob: patient.birthDate?.substring(0, 10),
        gender: mapGenderToScriptSure(patient.gender),
        addressLine1: defaultAddress?.line1?.substring(0, 35),
        city: defaultAddress?.city?.substring(0, 35),
        zip: defaultAddress?.postalCode?.replace(/\D/g, '').substring(0, 9),
        state: defaultAddress?.state?.substring(0, 2),
        home: homePhone,
        work: workPhone,
        cell: cellPhone,
        consent: false,
        patientStatusId: patient.active === false ? 6 : 0,

        // Optional patient fields
        patientIdExternal: patient.id?.replaceAll('-', ''),
        middleName: patient.middleName?.substring(0, 35),
        suffix: suffix?.substring(0, 35),
        ssn: patient.ssn?.replace(/\D/g, ''),
        addressLine2: defaultAddress?.line2?.substring(0, 35),
        email: patient.primaryEmailValue?.substring(0, 80),
    };

    return removeEmptyValues(data);
};

// Failures that come from how the account, location or user is set up, rather than from a request
const SETUP_FAILURE_REASONS = ['not-enabled', 'no-practice-id', 'not-a-prescriber', 'no-prescriber-id', 'on-behalf-not-set-up'];

const nameOf = (value) => (typeof value === 'string' ? value : value?.display || value?.name) || null;

// The reason to show for a failed request: the server's own detail or error when it answered, else the error's message
const failureDetailOf = (err) => (err?.response ? err.response.data?.detail || err.response.data?.error : err?.message);

/**
 * The message shown to the user for an e-Prescribe failure: what's missing or what failed, and who can fix it
 * @param {string} reason - One of the e-Prescribe failure reasons
 * @param {Object} [context] - { status, detail, location, provider }; names are filled in when given
 * @returns {string}
 */
const describeScriptSureFailure = (reason, context = {}) => {
    const { status, detail } = context;
    const detailSuffix = detail ? `: ${detail}` : '';
    const locationName = nameOf(context.location);
    const providerName = nameOf(context.provider);

    switch (reason) {
        case 'not-enabled':
            return "e-Prescribe isn't turned on for this account. Contact BestNotes support.";
        case 'no-practice-id':
            return `${locationName || 'This location'} has no ScriptSure practice ID. An administrator can add it in Settings → Locations, or switch to a location that has one.`;
        case 'not-a-prescriber':
            return "Your user isn't set up as a provider, so you can't e-prescribe. An administrator can change this in User manager.";
        case 'no-prescriber-id':
            return "You don't have e-Prescribe access yet. An administrator can request it for you in User manager.";
        case 'on-behalf-not-set-up':
            return `${providerName || 'The provider'}, who you prescribe on behalf of, doesn't have e-Prescribe access yet. An administrator can request it in User manager.`;
        case 'registration-failed':
            return `e-Prescribe couldn't register this patient${detailSuffix}. Try again, and contact BestNotes support if it keeps happening.`;
        case 'sign-in-failed':
            if (status === 401) return 'Your e-Prescribe session expired. Try again.';
            return `e-Prescribe sign-in failed${detailSuffix}. Try again, and contact BestNotes support if it keeps happening.`;
        case 'unexpected':
            return `Something went wrong opening e-Prescribe${detailSuffix}. Try again, and contact BestNotes support if it keeps happening.`;
        case 'no-session-url':
        default:
            return "e-Prescribe didn't return a session to open. Try again, and contact BestNotes support if it keeps happening.";
    }
};

/**
 * Log an error that isn't an HTTP failure; HTTP failures are already logged by the HTTP error interceptor
 * @param {string} operation - What was being done, for the log summary
 * @param {Error} err
 */
const logNonHttpError = (operation, err) => {
    if (err?.response) return;
    try {
        $log.error(`useScriptSureClinicalSync: ${operation} failed`, err?.message, err?.stack);
    } catch {
        // A logging failure must never break the sync it reports on
    }
};

/**
 * Composable for syncing clinical data (allergies, diagnoses, and medications) to ScriptSure.
 * Provides shared functionality for AllergiesWidget, DiagnosisListWidget, MedicationsListWidget, and BnMedicationsCard.
 */
export function useScriptSureClinicalSync() {
    const authStore = useAuthStore();
    const { syncAllergy, updateAllergy, syncDiagnosis, updateDiagnosis, syncMedications, signIn, searchAllergen, updatePatient } = useScriptSureApi();

    // Track resources currently being synced to prevent duplicate syncs
    const allergiesBeingSynced = ref(new Set());
    const diagnosesBeingSynced = ref(new Set());

    // In-memory cache of scriptSurePatientId by FHIR patient ID
    const patientIdCache = new Map();

    // Stores the scriptSureUrl from the most recent new-patient registration signIn response,
    // so connectToScriptSure can skip a redundant second signIn call for new patients.
    let lastRegistrationUrl = null;

    // Cache for acting-on-behalf prescriber ID
    const actingOnBehalfPrescriberIdCache = ref(null);
    const actingOnBehalfLoaded = ref(false);

    // Check if ScriptSure is enabled for the account
    const isScriptSureEnabled = computed(() => authStore.account?.scriptSureEnabled);

    // Prescriber context computed from auth store
    const practiceId = computed(() => authStore.brand?.location?.scriptSurePracticeId);
    const isProvider = computed(() => authStore.user?.user?.isProvider);
    const ownPrescriberId = computed(() => authStore.practitioner?.scriptSurePrescriberId);
    const actingOnBehalfOf = computed(() => authStore.practitioner?.scriptSureActingOnBehalfOf);
    const isActingOnBehalf = computed(() => !isProvider.value && !!actingOnBehalfOf.value?.id);

    const prescriberId = computed(() => {
        if (isProvider.value && ownPrescriberId.value) {
            return ownPrescriberId.value;
        }
        if (isActingOnBehalf.value && actingOnBehalfPrescriberIdCache.value) {
            return actingOnBehalfPrescriberIdCache.value;
        }
        return null;
    });

    /**
     * Check if ScriptSure credentials are available (account enabled + user has prescriber access)
     * @returns {boolean}
     */
    const hasScriptSureCredentials = computed(() => {
        return isScriptSureEnabled.value && !!practiceId.value && !!prescriberId.value;
    });

    // Allergen search signs in as the calling user, so it needs the user's own ScriptSure identity, not the lazy-loaded prescriber ID hasScriptSureCredentials needs
    const canSearchScriptSure = computed(() => !!isScriptSureEnabled.value && (!!ownPrescriberId.value || isActingOnBehalf.value));

    /**
     * Fetch the acting-on-behalf practitioner's prescriber ID (cached after first call)
     */
    const loadActingOnBehalfPrescriberId = async () => {
        if (actingOnBehalfLoaded.value) return;
        if (!isActingOnBehalf.value || !actingOnBehalfOf.value?.id) {
            actingOnBehalfLoaded.value = true;
            return;
        }

        try {
            const response = await $httpFhirApi.get(`Practitioner/${actingOnBehalfOf.value.id}?_elements=scriptSurePrescriberId`);
            if (response.data?.scriptSurePrescriberId) {
                actingOnBehalfPrescriberIdCache.value = response.data.scriptSurePrescriberId;
            }
        } catch (err) {
            logNonHttpError('fetching the acting-on-behalf practitioner', err);
        } finally {
            actingOnBehalfLoaded.value = true;
        }
    };

    /**
     * Write the one e-Prescribe log entry for a failure and return the message to show for it.
     * Setup reasons are logged as warnings, everything else as errors. No patient demographics are logged.
     * @param {string} reason - One of the e-Prescribe failure reasons
     * @param {Object} context - { status, detail, surface, patient, location, provider }
     * @returns {{ reason: string, message: string }}
     */
    const reportLaunchFailure = (reason, context = {}) => {
        const { status, detail, surface, patient, location = authStore.brand?.location } = context;
        const message = describeScriptSureFailure(reason, { ...context, location });
        const entry = JSON.stringify({ reason, surface, patientId: patient?.id, locationId: location?.id, status, detail });

        try {
            if (SETUP_FAILURE_REASONS.includes(reason)) {
                $log.warning(`e-Prescribe launch failed: ${reason}`, entry);
            } else {
                $log.error(`e-Prescribe launch failed: ${reason}`, entry);
            }
        } catch {
            // A logging failure must never block the user's message
        }

        return { reason, message };
    };

    /**
     * reportLaunchFailure for a caught error: takes the HTTP status and the reason to show from the error itself.
     * @param {string} reason - 'sign-in-failed' or 'unexpected'
     * @param {Error} err - The caught error
     * @param {Object} context - { surface, patient }
     * @returns {{ reason: string, message: string }}
     */
    const reportLaunchError = (reason, err, context = {}) => reportLaunchFailure(reason, { ...context, status: err?.response?.status, detail: failureDetailOf(err) });

    /**
     * Resolve a patient's ScriptSure ID, registering the patient if needed, and say why when it can't be done.
     * - Returns { scriptSurePatientId } from the cache, the patient or FHIR without signing in
     * - Returns { missingFields: [...] } if patient data is incomplete
     * - Returns { failure: { reason, message, status?, detail? } } otherwise
     * Setup failures are logged only for source 'launch'; registration failures are always logged.
     *
     * @param {Object} patient - The full Patient FHIR resource
     * @param {Object} [options]
     * @param {string} [options.source] - 'launch' for an e-Prescribe button, 'background' for syncs
     * @param {string} [options.surface] - Where the launch came from, for the log entry
     * @returns {Promise<{scriptSurePatientId: string}|{missingFields: string[]}|{failure: Object}>}
     */
    const resolveScriptSurePatient = async (patient, { source = 'background', surface } = {}) => {
        const fail = (reason, { status, detail, provider, log = true } = {}) => {
            const context = { status, detail, surface, patient, provider, location: authStore.brand?.location };
            const message = log ? reportLaunchFailure(reason, context).message : describeScriptSureFailure(reason, context);
            const failure = { reason, message };
            if (status !== undefined) failure.status = status;
            if (detail !== undefined) failure.detail = detail;
            return { failure };
        };
        const isLaunch = source === 'launch';

        if (!patient?.id) return fail('registration-failed', { log: isLaunch });

        // Ensure acting-on-behalf prescriber ID is loaded
        if (isActingOnBehalf.value && !actingOnBehalfLoaded.value) {
            await loadActingOnBehalfPrescriberId();
        }

        // Check prerequisites, in the order an administrator would fix them
        if (!isScriptSureEnabled.value) return fail('not-enabled', { log: isLaunch });
        if (!practiceId.value) return fail('no-practice-id', { log: isLaunch });
        if (!isProvider.value && !isActingOnBehalf.value) return fail('not-a-prescriber', { log: isLaunch });
        if (isProvider.value && !ownPrescriberId.value) return fail('no-prescriber-id', { log: isLaunch });
        if (!prescriberId.value) return fail('on-behalf-not-set-up', { provider: actingOnBehalfOf.value, log: isLaunch });

        // Check in-memory cache
        const cached = patientIdCache.get(patient.id);
        if (cached) return { scriptSurePatientId: cached };

        // Check on the patient object itself
        if (patient.scriptSurePatientId) {
            patientIdCache.set(patient.id, patient.scriptSurePatientId);
            return { scriptSurePatientId: patient.scriptSurePatientId };
        }

        // Fetch from FHIR in case it was set by another session/component
        const existingId = await getScriptSurePatientId(patient.id);
        if (existingId) {
            patientIdCache.set(patient.id, existingId);
            return { scriptSurePatientId: existingId };
        }

        // Patient is not registered — validate data
        const validationErrors = validatePatientForScriptSure(patient);
        if (validationErrors.length > 0) {
            return { missingFields: validationErrors };
        }

        // Build sign-in payload and register
        let response;
        try {
            const signInData = buildSignInDataForPatient(patient, {
                practiceId: practiceId.value,
                prescriberId: prescriberId.value,
                isActingOnBehalf: isActingOnBehalf.value,
            });
            // Lets the server save the ScriptSure ID onto the Patient; kept out of buildSignInDataForPatient, which also builds update-patient
            signInData.fhirPatientId = patient.id;

            response = await signIn(signInData);
        } catch (err) {
            return fail('registration-failed', { status: err?.response?.status, detail: failureDetailOf(err) });
        }

        if (!response?.scriptSurePatientId) {
            return fail('registration-failed', { detail: response?.detail || response?.error });
        }

        // The server saved the ID onto the Patient, so registering needs no permission to edit the patient record
        const scriptSurePatientId = String(response.scriptSurePatientId);

        // Update local object and cache
        patient.scriptSurePatientId = scriptSurePatientId;
        patientIdCache.set(patient.id, scriptSurePatientId);

        // Stash the URL so callers can skip a redundant signIn for new registrations
        lastRegistrationUrl = response.scriptSureUrl || null;

        return { scriptSurePatientId };
    };

    /**
     * Ensure a patient is registered in ScriptSure, for background syncs. Returns the scriptSurePatientId if successful.
     * - Returns the cached/existing ID if already registered
     * - Auto-registers the patient if not yet registered and data is valid
     * - Returns { missingFields: [...] } if patient data is incomplete
     * - Returns null for every failure; user-facing launches use resolveScriptSurePatient for the reason
     *
     * @param {Object} patient - The full Patient FHIR resource
     * @returns {Promise<string|{missingFields: string[]}|null>} scriptSurePatientId, missing fields object, or null
     */
    const ensureScriptSurePatient = async (patient) => {
        const result = await resolveScriptSurePatient(patient);
        if (result.missingFields) return { missingFields: result.missingFields };
        return result.scriptSurePatientId || null;
    };

    // "No Known Allergies" SNOMED code
    const NO_KNOWN_ALLERGY_CODE = '716186003';

    /**
     * Map allergy category to ScriptSure allergyType
     * If the allergy has FDB data from ScriptSure allergen search, use that directly.
     * Otherwise fall back to heuristic mapping:
     * 0=Singular Chemical, 1=Brand, 2=Generic, 3=Drug Class, 4=RxNorm, 5=NDC, 100=No Known Allergies
     * @param {Object} allergy - The AllergyIntolerance resource
     * @returns {number} The ScriptSure allergyType code
     */
    /**
     * Parse FDB coding stored as "allergyId|allergyType" in the code field
     * @param {Object} fdbCoding - The coding object with system http://fhir.bestnotes.com/scriptsure/fdb
     * @returns {{ allergyId: string, allergyType: number }|null}
     */
    const parseFdbCoding = (fdbCoding) => {
        if (!fdbCoding?.code) return null;
        const parts = fdbCoding.code.split('|');
        if (parts.length !== 2) return null;
        return { allergyId: parts[0], allergyType: parseInt(parts[1]) };
    };

    const mapAllergyTypeToScriptSure = (allergy) => {
        // Use FDB allergyType if available (set when user selects from ScriptSure search)
        const fdbCoding = allergy.code?.coding?.find((c) => c.system === 'http://fhir.bestnotes.com/scriptsure/fdb');
        const fdbData = parseFdbCoding(fdbCoding);
        if (fdbData) {
            return fdbData.allergyType;
        }

        const rxnormCode = allergy.code?.coding?.find((c) => c.system === 'http://www.nlm.nih.gov/research/umls/rxnorm')?.code;
        if (rxnormCode) {
            return 4; // RxNorm
        }
        if (allergy.category?.[0] === 'medication') {
            return 2; // Generic
        }
        return 0; // Singular Chemical
    };

    /**
     * Map allergy category to ScriptSure adverseEventCode (SNOMED codes)
     * Drug allergy: 416098002, Food allergy: 414285001, Other: 420134006
     * @param {string} category - The allergy category
     * @returns {string} The SNOMED adverse event code
     */
    const mapAdverseEventCodeToScriptSure = (category) => {
        const codeMap = {
            medication: '416098002', // Drug allergy
            food: '414285001', // Food allergy
            environment: '420134006', // Allergy to substance
            biologic: '420134006', // Allergy to substance
        };
        return codeMap[category] || '420134006';
    };

    /**
     * Map diagnosis code system to ScriptSure codingSystem
     * 0=Unknown, 1=ICD9, 2=ICD10
     * @param {string} system - The FHIR code system URL
     * @returns {number} The ScriptSure codingSystem code
     */
    const mapCodingSystemToScriptSure = (system) => {
        const systemLower = system?.toLowerCase() || '';
        if (systemLower.includes('icd10') || systemLower.includes('icd-10')) {
            return 2; // ICD10
        }
        if (systemLower.includes('icd9') || systemLower.includes('icd-9')) {
            return 1; // ICD9
        }
        // DSM-5 codes should also use ICD-10 format
        if (systemLower.includes('dsm5') || systemLower.includes('dsm')) {
            return 2; // ICD10
        }
        return 0; // Unknown
    };

    /**
     * Sync an allergy to ScriptSure and save the ScriptSure ID back to FHIR
     * @param {Object} allergy - The AllergyIntolerance resource
     * @param {string|number} scriptSurePatientId - The ScriptSure patient ID
     * @returns {Promise<boolean>} Whether the sync was successful
     */
    const syncAllergyToScriptSure = async (allergy, scriptSurePatientId) => {
        const allergyName = allergy.code?.text || allergy.code?.coding?.[0]?.display;

        if (!scriptSurePatientId || !allergyName) {
            return false;
        }

        // Skip "no known allergies" entries
        if (allergy.code?.coding?.[0]?.code === NO_KNOWN_ALLERGY_CODE) {
            return false;
        }

        try {
            const rxnormCode = allergy.code?.coding?.find((c) => c.system === 'http://www.nlm.nih.gov/research/umls/rxnorm')?.code;
            const ndcCode = allergy.code?.coding?.find((c) => c.system === 'http://hl7.org/fhir/sid/ndc')?.code;
            const fdbCoding = allergy.code?.coding?.find((c) => c.system === 'http://fhir.bestnotes.com/scriptsure/fdb');
            const fdbData = parseFdbCoding(fdbCoding);

            const allergyData = {
                patientId: parseInt(scriptSurePatientId),
                allergyId: fdbData?.allergyId || rxnormCode || allergyName,
                allergyType: mapAllergyTypeToScriptSure(allergy),
                adverseEventCode: mapAdverseEventCodeToScriptSure(allergy.category?.[0]),
                name: allergyName,
                archive: allergy.clinicalStatus?.coding?.[0]?.code === 'active' ? 0 : 1,
            };

            // Add drug codes for drug-allergy interaction checking
            if (rxnormCode) {
                allergyData.rxnorm = rxnormCode;
            }
            if (ndcCode) {
                allergyData.ndc = ndcCode;
            }
            if (allergy.onset?.string) {
                // ScriptSure expects ISO 8601 datetime format
                const onsetDate = allergy.onset.string;
                if (onsetDate.includes('-') && onsetDate.length === 10) {
                    allergyData.onsetDate = `${onsetDate}T12:00:00+00:00`;
                } else if (!onsetDate.includes('T')) {
                    allergyData.onsetDate = `${onsetDate}T12:00:00+00:00`;
                } else {
                    allergyData.onsetDate = onsetDate;
                }
            }
            if (allergy.reaction?.[0]?.manifestation?.[0]?.text) {
                allergyData.comment = allergy.reaction[0].manifestation[0].text;
            }

            let response;
            if (allergy.scriptSureAllergyId) {
                // Update existing allergy in ScriptSure
                allergyData.id = parseInt(allergy.scriptSureAllergyId);
                response = await updateAllergy(allergyData);
            } else {
                // Create new allergy in ScriptSure
                response = await syncAllergy(allergyData);
            }

            // Save the ScriptSure allergy ID back to FHIR if this is a new sync
            const scriptSureAllergyIdFromResponse = response?.allergy?.id;
            if (scriptSureAllergyIdFromResponse && !allergy.scriptSureAllergyId) {
                await $httpFhirApi.patch(`AllergyIntolerance/${allergy.id}`, [{ op: 'add', path: '/scriptSureAllergyId', value: String(scriptSureAllergyIdFromResponse) }]);
                // Update local object so subsequent syncs know it's already synced
                allergy.scriptSureAllergyId = String(scriptSureAllergyIdFromResponse);
            }

            return true;
        } catch (err) {
            logNonHttpError('syncing an allergy', err);
            return false;
        }
    };

    /**
     * Sync a diagnosis to ScriptSure and save the ScriptSure ID back to FHIR
     * @param {Object} diagnosis - The Condition resource
     * @param {string|number} scriptSurePatientId - The ScriptSure patient ID
     * @returns {Promise<boolean>} Whether the sync was successful
     */
    const syncDiagnosisToScriptSure = async (diagnosis, scriptSurePatientId) => {
        const diagnosisCode = diagnosis.code?.coding?.[0]?.code;
        const diagnosisName = diagnosis.code?.text || diagnosis.code?.coding?.[0]?.display;

        if (!scriptSurePatientId || !diagnosisCode || !diagnosisName) {
            return false;
        }

        try {
            const clinicalStatusCode = diagnosis.clinicalStatus?.coding?.[0]?.code || 'active';
            const isActive = ['active', 'recurrence', 'relapse', 'remission'].includes(clinicalStatusCode);

            // Map verificationStatus to ScriptSure diagnosisTypeId
            // 1 = Initial Diagnosis, 2 = Final Diagnosis
            const verificationCode = diagnosis.verificationStatus?.coding?.[0]?.code;
            const diagnosisTypeId = verificationCode === 'confirmed' ? 2 : 1;

            const diagnosisData = {
                patientId: parseInt(scriptSurePatientId),
                conceptId: diagnosisCode,
                codingSystem: mapCodingSystemToScriptSure(diagnosis.code?.coding?.[0]?.system),
                name: diagnosisName,
                archive: isActive ? 0 : 1,
                diagnosisTypeId,
                short: diagnosisCode,
                long: diagnosisName,
            };

            // Map FHIR Condition.recordedDate to ScriptSure startDate
            if (diagnosis.recordedDate) {
                diagnosisData.startDate = diagnosis.recordedDate;
            }

            // Add comment from notes if available
            const noteText = diagnosis.note?.[0]?.text;
            if (noteText) {
                diagnosisData.comment = noteText;
            }

            // Add onset date as comment supplement if present and no note
            if (!noteText && diagnosis.onset?.dateTime) {
                diagnosisData.comment = `Onset: ${diagnosis.onset.dateTime}`;
            }

            // Add recorder info if available
            if (diagnosis.recorder?.display) {
                diagnosisData.doctorName = diagnosis.recorder.display;
            }

            let response;
            if (diagnosis.scriptSureDiagnosisId) {
                // Update existing diagnosis in ScriptSure
                diagnosisData.id = parseInt(diagnosis.scriptSureDiagnosisId);
                // Send the stored encounter ID so ScriptSure can match the record
                if (diagnosis.scriptSureEncounterId) {
                    diagnosisData.encounterId = parseInt(diagnosis.scriptSureEncounterId);
                }
                response = await updateDiagnosis(diagnosisData);
            } else {
                // Create new diagnosis in ScriptSure
                response = await syncDiagnosis(diagnosisData);
            }

            // Save the ScriptSure diagnosis ID and encounter ID back to FHIR if this is a new sync
            const scriptSureDiagnosisIdFromResponse = response?.diagnosis?.id;
            const scriptSureEncounterIdFromResponse = response?.encounterId;
            if (scriptSureDiagnosisIdFromResponse && !diagnosis.scriptSureDiagnosisId) {
                const patchOps = [{ op: 'add', path: '/scriptSureDiagnosisId', value: String(scriptSureDiagnosisIdFromResponse) }];
                if (scriptSureEncounterIdFromResponse) {
                    patchOps.push({ op: 'add', path: '/scriptSureEncounterId', value: String(scriptSureEncounterIdFromResponse) });
                }
                await $httpFhirApi.patch(`Condition/${diagnosis.id}`, patchOps);
                diagnosis.scriptSureDiagnosisId = String(scriptSureDiagnosisIdFromResponse);
                if (scriptSureEncounterIdFromResponse) {
                    diagnosis.scriptSureEncounterId = String(scriptSureEncounterIdFromResponse);
                }
            }

            return true;
        } catch (err) {
            logNonHttpError('syncing a diagnosis', err);
            return false;
        }
    };

    /**
     * Sync a medication to ScriptSure and save the ScriptSure prescription ID back to FHIR
     * @param {Object} medication - The MedicationRequest resource
     * @param {string|number} scriptSurePatientId - The ScriptSure patient ID
     * @returns {Promise<{success: boolean, drugNotFound?: boolean, errors?: string[]}>} Result with drugNotFound flag when the RxNorm code wasn't found in ScriptSure's FDB, and the server's errors when it reports a failure
     */
    const syncMedicationToScriptSure = async (medication, scriptSurePatientId) => {
        const drugName = medication.medication?.CodeableConcept?.text;

        if (!scriptSurePatientId || !drugName) {
            return { success: false };
        }

        try {
            // Collect all RxNorm codes — ScriptSure needs a specific clinical drug code (SCD/SBD),
            // not just the ingredient-level code. Send all so the backend can try each one.
            const rxnormCodes =
                medication.medication?.CodeableConcept?.coding
                    ?.filter((c) => c.system === 'http://www.nlm.nih.gov/research/umls/rxnorm')
                    ?.map((c) => c.code)
                    ?.filter(Boolean) || [];

            // Also grab NDC code as a fallback for drug lookup
            const ndcCode = medication.medication?.CodeableConcept?.coding?.find((c) => c.system === 'http://hl7.org/fhir/sid/ndc')?.code;

            // Get the most recent reconciliation date from the array
            const lastReconciliation = medication.reconciliation?.length ? medication.reconciliation[medication.reconciliation.length - 1] : null;

            // Determine if the medication is archived:
            // - FHIR status of stopped/cancelled/completed, OR
            // - effective.end date is today or in the past (how archival works in the UI)
            const inactiveStatuses = ['stopped', 'cancelled', 'completed'];
            const hasInactiveStatus = inactiveStatuses.includes(medication.status);
            const hasEndedEffective = (() => {
                if (!medication.effective?.end) return false;
                const endDate = new Date(medication.effective.end + 'T23:59:59');
                return endDate <= new Date();
            })();
            const isArchived = hasInactiveStatus || hasEndedEffective;

            const medicationData = {
                drugName,
                status: medication.status || 'active',
                archive: isArchived,
                reconcileDate: lastReconciliation?.date || new Date().toISOString(),
            };

            // Add ScriptSure prescription ID and encounter ID if this is an update
            if (medication.scriptSurePrescriptionId) {
                medicationData.scriptSurePrescriptionId = medication.scriptSurePrescriptionId;
            }
            if (medication.scriptSureEncounterId) {
                medicationData.scriptSureEncounterId = medication.scriptSureEncounterId;
            }

            // Add drug codes — send all RxNorm codes so backend can try each
            if (rxnormCodes.length > 0) {
                medicationData.rxnormCodes = rxnormCodes;
            }
            if (ndcCode) {
                medicationData.ndc = ndcCode;
            }
            const carrier = carrierOf(medication);
            if (carrier?.text) {
                medicationData.dose = carrier.text;
            }
            if (carrier?.timing?.code?.text) {
                medicationData.frequency = carrier.timing.code.text;
            }
            if (medication.effective?.start) {
                medicationData.startDate = medication.effective.start;
            }
            if (medication.effective?.end) {
                medicationData.endDate = medication.effective.end;
            }

            // Ensure acting-on-behalf prescriber ID is loaded before accessing prescriberId
            if (isActingOnBehalf.value && !actingOnBehalfLoaded.value) {
                await loadActingOnBehalfPrescriberId();
            }

            const response = await syncMedications({
                patientId: parseInt(scriptSurePatientId),
                practiceId: practiceId.value,
                prescriberId: prescriberId.value,
                medications: [medicationData],
            });

            // The server reports per-medication failures in a 200 and has already logged them
            if (response?.success === false) {
                return { success: false, errors: response.errors || [] };
            }

            // Save the ScriptSure prescription ID and encounter ID back to FHIR if this is a new sync
            const result = response?.results?.[0];
            const encounterId = response?.encounterId;
            if (result?.prescriptionId && !medication.scriptSurePrescriptionId) {
                const patchOps = [{ op: 'add', path: '/scriptSurePrescriptionId', value: String(result.prescriptionId) }];
                if (encounterId) {
                    patchOps.push({ op: 'add', path: '/scriptSureEncounterId', value: String(encounterId) });
                }
                await $httpFhirApi.patch(`MedicationRequest/${medication.id}`, patchOps);
                // Update local object so subsequent syncs know it's already synced
                medication.scriptSurePrescriptionId = String(result.prescriptionId);
                if (encounterId) {
                    medication.scriptSureEncounterId = String(encounterId);
                }
            }

            return { success: true, drugNotFound: result?.drugNotFound || false };
        } catch (err) {
            logNonHttpError('syncing a medication', err);
            return { success: false };
        }
    };

    /**
     * @param {Object} options
     * @param {Object} options.resource - The FHIR resource (AllergyIntolerance or MedicationRequest)
     * @param {Object} [options.patient] - The Patient FHIR resource (used for ensureScriptSurePatient)
     * @param {string} [options.scriptSurePatientId] - Pre-resolved ScriptSure patient ID (takes priority)
     * @param {string} [options.patientId] - FHIR patient ID (fallback for getScriptSurePatientId)
     * @param {Function} options.syncFn - The sync function to call (e.g. syncAllergyToScriptSure)
     * @param {string} options.operationLabel - Label for error logging (e.g. 'allergy inactivation')
     * @param {Function} [options.onMissingFields] - Callback when ensureScriptSurePatient returns missing fields
     */
    const resolveAndSyncResource = async ({ resource, patient, scriptSurePatientId, patientId, syncFn, operationLabel, onMissingFields }) => {
        if (!isScriptSureEnabled.value || !resource?.id) return;

        const resolvedId = scriptSurePatientId || (patient ? await ensureScriptSurePatient(patient) : null) || (patientId ? await getScriptSurePatientId(patientId) : null);

        if (resolvedId && typeof resolvedId === 'object' && resolvedId.missingFields) {
            onMissingFields?.(resolvedId.missingFields);
            return;
        }

        if (typeof resolvedId === 'string' && resolvedId) {
            syncFn(resource, resolvedId).catch((err) => logNonHttpError(`syncing ${operationLabel}`, err));
        }
    };

    /**
     * Handle syncing allergies after a FHIR save operation
     * Filters and syncs allergies that need to be synced to ScriptSure
     * @param {Array} allergies - Array of AllergyIntolerance resources
     * @param {string|number} scriptSurePatientId - The ScriptSure patient ID
     * @returns {Promise<void>}
     */
    const handleAllergiesSaved = async (allergies, scriptSurePatientId) => {
        if (!isScriptSureEnabled.value || !scriptSurePatientId) {
            return;
        }

        const allergiesToSync = allergies.filter((allergy) => {
            // Must be an AllergyIntolerance resource
            if (allergy.resourceType !== 'AllergyIntolerance') return false;
            // Must have an id (saved to FHIR)
            if (!allergy.id) return false;
            // Must have allergen text or display
            if (!allergy.code?.text && !allergy.code?.coding?.[0]?.display) return false;
            // Skip "no known allergies" entries
            if (allergy.code?.coding?.[0]?.code === NO_KNOWN_ALLERGY_CODE) return false;
            // Skip if already being synced
            if (allergiesBeingSynced.value.has(allergy.id)) return false;
            return true;
        });

        for (const allergy of allergiesToSync) {
            allergiesBeingSynced.value.add(allergy.id);

            syncAllergyToScriptSure(allergy, scriptSurePatientId)
                .catch((err) => {
                    logNonHttpError('background allergy sync', err);
                })
                .finally(() => {
                    allergiesBeingSynced.value.delete(allergy.id);
                });
        }
    };

    /**
     * Handle syncing diagnoses after a FHIR save operation
     * Filters and syncs diagnoses that need to be synced to ScriptSure
     * @param {Array} diagnoses - Array of Condition resources
     * @param {string|number} scriptSurePatientId - The ScriptSure patient ID
     * @returns {Promise<void>}
     */
    const handleDiagnosesSaved = async (diagnoses, scriptSurePatientId) => {
        if (!isScriptSureEnabled.value || !scriptSurePatientId) {
            return;
        }

        const diagnosesToSync = diagnoses.filter((diagnosis) => {
            // Must be a Condition resource
            if (diagnosis.resourceType !== 'Condition') return false;
            // Must have an id (saved to FHIR)
            if (!diagnosis.id) return false;
            // Must have diagnosis code
            if (!diagnosis.code?.coding?.[0]?.code) return false;
            // Skip if already being synced
            if (diagnosesBeingSynced.value.has(diagnosis.id)) return false;
            return true;
        });

        for (const diagnosis of diagnosesToSync) {
            diagnosesBeingSynced.value.add(diagnosis.id);

            syncDiagnosisToScriptSure(diagnosis, scriptSurePatientId)
                .catch((err) => {
                    logNonHttpError('background diagnosis sync', err);
                })
                .finally(() => {
                    diagnosesBeingSynced.value.delete(diagnosis.id);
                });
        }
    };

    /**
     * Fetch the scriptSurePatientId for a patient
     * @param {string} patientId - The FHIR Patient ID
     * @returns {Promise<string|null>} The ScriptSure patient ID or null
     */
    const getScriptSurePatientId = async (patientId) => {
        if (!isScriptSureEnabled.value || !patientId) {
            return null;
        }

        try {
            const response = await $httpFhirApi.get(`Patient/${patientId}?_elements=scriptSurePatientId`);
            return response.data?.scriptSurePatientId || null;
        } catch (err) {
            logNonHttpError('fetching scriptSurePatientId', err);
            return null;
        }
    };

    /**
     * Sync patient demographics to ScriptSure
     * @param {Object} patient - The Patient FHIR resource (must have scriptSurePatientId to trigger sync)
     * @returns {Promise<boolean>} Whether the sync was attempted (false if skipped due to guards)
     */
    const syncPatientDemographics = async (patient) => {
        if (!isScriptSureEnabled.value || !patient?.scriptSurePatientId || patient.resourceType !== 'Patient') {
            return false;
        }

        if (isActingOnBehalf.value && !actingOnBehalfLoaded.value) {
            await loadActingOnBehalfPrescriberId();
        }

        if (!practiceId.value || !prescriberId.value) {
            return false;
        }

        try {
            const payload = buildSignInDataForPatient(patient, {
                practiceId: practiceId.value,
                prescriberId: prescriberId.value,
                isActingOnBehalf: isActingOnBehalf.value,
            });
            payload.scriptSurePatientId = patient.scriptSurePatientId;

            await updatePatient(payload);
            return true;
        } catch (err) {
            logNonHttpError('syncing patient demographics', err);
            return false;
        }
    };

    return {
        // State
        isScriptSureEnabled,
        hasScriptSureCredentials,
        canSearchScriptSure,
        allergiesBeingSynced,
        diagnosesBeingSynced,

        // Constants
        NO_KNOWN_ALLERGY_CODE,

        // Mapping functions (exported for use in BnMedicationsCard bulk sync)
        mapAllergyTypeToScriptSure,
        mapAdverseEventCodeToScriptSure,
        mapCodingSystemToScriptSure,

        // Single resource sync functions
        syncAllergyToScriptSure,
        syncDiagnosisToScriptSure,
        syncMedicationToScriptSure,
        syncPatientDemographics,
        resolveAndSyncResource,

        // ScriptSure FDB allergen search
        searchAllergen,

        // Batch sync handlers for DataProvider @saved events
        handleAllergiesSaved,
        handleDiagnosesSaved,

        // Patient registration
        ensureScriptSurePatient,
        resolveScriptSurePatient,
        reportLaunchFailure,
        reportLaunchError,
        describeScriptSureFailure,
        loadActingOnBehalfPrescriberId,
        consumeLastRegistrationUrl: () => {
            const url = lastRegistrationUrl;
            lastRegistrationUrl = null;
            return url;
        },

        // Utility functions
        getScriptSurePatientId,
        validatePatientForScriptSure: (patient) => validatePatientForScriptSure(patient),
        buildSignInDataForPatient: (patient) =>
            buildSignInDataForPatient(patient, {
                practiceId: practiceId.value,
                prescriberId: prescriberId.value,
                isActingOnBehalf: isActingOnBehalf.value,
            }),
        formatPhoneForScriptSure,
        mapGenderToScriptSure,
    };
}
